














import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import BaseMixin from '@/components/mixins/base';
import DxFilterBuilder from 'devextreme-vue/filter-builder';

import { first, isBoolean, isEqual, isNil, isNumber, isString } from 'lodash';

import {
	TCubeDimension, TCubeMeasure, TCubeMemberType
} from '@cubejs-client/core';
import { isDate } from 'moment';
import { formatDate } from '@/chartHelper';



@Component({
	components: {
		DxFilterBuilder
	}
})
export default class FilterBuilder extends BaseMixin {

	@Prop({ type: Array, default: () => [] }) dimensions!: TCubeDimension[];
	@Prop({ type: Array, default: () => [] }) measures!: TCubeMeasure[];

	@Prop({ type: Array, default: () => [] }) filters!: any[];
	@Prop({ type: Function, default: () => () => void 0 }) setFilters!: (cubeFilter: CubeJsFilter) => void;

	internalFilter: any = [];
	dataTables: string[] = [];

	created() {
		// convert cubejs filter to dxFilter
		const f = this.convertCubeJsFilterToDxFilter(this.filters);
		this.internalFilter = f;
		this.dataTables = this.dimensions.map(d => d.name.split('.')[0]).concat(this.measures.map(m => m.name.split('.')[0])).filter((v, i, a) => a.indexOf(v) === i);
	}

	mounted() {
		this.setupFieldDropdownInterceptor();
	}

	//#region Field Dropdown Search Management

	setupFieldDropdownInterceptor() {
		const container = this.$refs.filterBuilderContainer as HTMLElement;
		if (!container) return;

		// Also listen for click events on field buttons
		container.addEventListener('click', (e) => {
			const target = e.target as HTMLElement;
			// Check if click is on a field button
			if (target.closest('.dx-filterbuilder-item-field')) {
				this.checkAndInjectSearchInput();
			}
		});
	}

	checkAndInjectSearchInput() {
		// Find all visible treeview popups
		const treeViewPopups = document.querySelectorAll('.dx-popup-wrapper:not(.dx-state-invisible) .dx-treeview');

		//need to check all popups because the can be other treeviews in the page
		treeViewPopups.forEach((treeView) => {
			const popup = treeView.closest('.dx-popup-wrapper');
			if (!popup) return;
			// Check if this popup already has search input
			if (popup.querySelector('.dx-filterbuilder-field-search') || popup.querySelector('.dx-textbox')) return;
			// Verify this is a field selector dropdown by checking if it's triggered from a field button
			// We do this by checking if the treeview contains items that match our fields
			const treeViewItems = treeView.querySelectorAll('.dx-treeview-item');
			if (treeViewItems.length === 0) return;

			// Check if any item text matches our field captions
			let isFieldDropdown = false;
			const firstItemText = treeViewItems[0]?.textContent?.trim();
			if (firstItemText) {
				isFieldDropdown = this.fields.some(f => f.caption === firstItemText);
			}

			if (!isFieldDropdown) return;

			// Inject search input
			this.injectSearchInput(popup as HTMLElement, treeView as HTMLElement);
		});
	}

	injectSearchInput(popup: HTMLElement, treeView: HTMLElement) {
		const content = popup.querySelector('.dx-popup-content');
		if (!content) return;

		// Create search input container
		const searchContainer = document.createElement('div');
		searchContainer.className = 'dx-filterbuilder-field-search';
		searchContainer.style.cssText = 'padding: 8px; border-bottom: 1px solid #ddd; background: #fff;';

		const searchInput = document.createElement('input');
		searchInput.type = 'text';
		searchInput.placeholder = this.$t('app.search');
		searchInput.className = 'dx-texteditor-input';
		searchInput.style.cssText = 'width: 100%; padding: 6px 8px; border: 1px solid #ddd; border-radius: 4px; box-sizing: border-box;';

		// Add search functionality
		searchInput.addEventListener('input', (e) => {
			const searchTerm = (e.target as HTMLInputElement).value.toLowerCase();
			this.filterTreeViewItems(treeView, searchTerm);
		});

		searchContainer.appendChild(searchInput);
		content.insertBefore(searchContainer, content.firstChild);

		// Focus the search input
		setTimeout(() => searchInput.focus(), 50);
	}

	filterTreeViewItems(treeView: HTMLElement, searchTerm: string) {
		const items = treeView.querySelectorAll('.dx-treeview-item');

		items.forEach((item) => {
			const itemText = item.textContent?.toLowerCase() || '';
			const itemElement = item as HTMLElement;

			if (searchTerm === '' || itemText.includes(searchTerm)) {
				itemElement.style.display = '';
			} else {
				itemElement.style.display = 'none';
			}
		});
	}

	//#endregion

	get filterOperationDescriptions() {
		const result = {
			between: 'Between',
			contains: 'Contains',
			endsWith: 'Ends with',
			equal: 'Equals',
			greaterThan: 'Greater than',
			greaterThanOrEqual: 'Greater than or equal to',
			isBlank: 'Is blank',
			isNotBlank: 'Is not blank',
			lessThan: 'Less than',
			lessThanOrEqual: 'Less than or equal to',
			notContains: 'Does not contain',
			notEqual: 'Does not equal',
			startsWith: 'Starts with'
		};
		Object.keys(result).forEach(k => {
			result[k] = this.$t(`filterOperationDescriptions.${k}`);
		});
		return result;
	}

	get groupOperations(): GroupOperator[] {
		return ['and', 'or'];
	}
	get groupOperationDescriptions() {
		const result: Record<string, string> = {};
		this.groupOperations.forEach(go => {
			result[go] = this.$t(`groupOperationDescriptions.${go}`);
		});
		return result;
	}

	get fields() {
		const result: IField[] = [];

		Array.prototype.push.apply(result, this.dimensions.map<IField>(d => {
			const field: IField = {
				caption: this.$t(d.name),
				dataField: d.name,
				dataType: this.convertDataTypeToDxFilter(d.type),
				filterOperations: this.filterOperatorsForDimension[d.type].dxFilterOperators
			};
			
			this.populateBoolLookup(d, field);
			
			return field;
		}));

		Array.prototype.push.apply(result, this.measures.map(m => {
			const field: IField = {
				caption: this.$t(m.name),
				dataField: m.name,
				dataType: this.convertDataTypeToDxFilter(m.type),
				filterOperations: this.filterOperatorsForMeasure[m.type].dxFilterOperators
			};
			
			this.populateBoolLookup(m, field);
			
			return field;
		}));
		return result.sort((r1, r2) => r1.caption.localeCompare(r2.caption));
	}

	private populateBoolLookup(m: TCubeMeasure | TCubeDimension, field: IField) {
		if (m.type === 'boolean') {
			field.lookup = {
				dataSource: [
					{ value: '1', text: this.$t('app.true') },
					{ value: '0', text: this.$t('app.false') }
				],
				valueExpr: 'value',
				displayExpr: 'text'
			};
		}
	}

	convertDataTypeToDxFilter(dataType: TCubeMemberType) {
		switch (dataType) {
			case 'time':
				return 'datetime';
			default:
				return dataType;
		}
	}

	get measuresSet() {
		const result: Record<string, boolean> = {};
		this.measures.forEach(m => {
			const measureName = m.name;
			result[measureName] = true;
		});
		return result;
	}

	get dimensionsSet() {
		const result: Record<string, boolean> = {};
		this.dimensions.forEach(d => {
			const dimensionName = d.name;
			result[dimensionName] = true;
		});
		return result;
	}

	/**
	 * Converts the dx-filter-builder filter to it's  [cubeJs representation](https://cube.dev/docs/query-format#filters-format)
	 */
	get cubeJsFilter(): CubeJsFilter | Record<string, never> {
		if (Array.isArray(this.internalFilter)) {
			if (this.dataTables.includes(this.internalFilter[0])) {
				const error = this.$t('filterBuilder.dxToCubeJS.invalidField');
				this.alertDialog(error);
			}
			else {
				const result = this.convertDxFilterToCubeJsFilter(this.internalFilter);
				return result;
			}
		}
		return {};
	}

	get filterOperatorsForDimension() {
		const result: Record<CubeJsDataTypes, {
			cubeJsOperators: CubeJsFilterOperatorType[],
			dxFilterOperators: DxFilterOperatorType[]
		}> = {} as any;
		// const result: Record<CubeJsFilterOperatorType, {
		// 	cubeJs: Record<CubeJsDataTypes, boolean>,
		// 	devExtremeOperator: Record<DxFilterOperatorType, boolean>
		// }> = {} as any;
		const operators = Object
			.values(this.cubeFilterOperatorsToDimensionMeasureAssociations)
			.filter(v => v.isDimensionCompatible);
		const cache: Record<CubeJsDataTypes, {
			cubeJsOperators: Record<CubeJsFilterOperatorType, boolean>,
			dxFilterOperators: Record<DxFilterOperatorType, boolean>
		}> = {} as any;
		operators.forEach(v => {
			const operator = v.operator;
			v.dataTypes.forEach(dt => {
				if (isNil(result[dt])) {
					result[dt] = {
						cubeJsOperators: [],
						dxFilterOperators: []
					};
					cache[dt] = {
						cubeJsOperators: {} as any,
						dxFilterOperators: {} as any
					};
				}
				cache[dt].cubeJsOperators[operator] = true;
				const dxFilterOperator = this.convertCubeJsOperatorToDxFilterOperator(operator);
				if (dxFilterOperator as string !== '')
					cache[dt].dxFilterOperators[dxFilterOperator] = true;
			});
		});

		Object
			.keys(result)
			.forEach((k: CubeJsDataTypes) => {
				result[k].cubeJsOperators = Object.keys(cache[k].cubeJsOperators) as CubeJsFilterOperatorType[];
				result[k].dxFilterOperators = Object.keys(cache[k].dxFilterOperators) as DxFilterOperatorType[];
			});

		return result;
	}

	get filterOperatorsForMeasure() {
		const result: Record<CubeJsDataTypes, {
			cubeJsOperators: CubeJsFilterOperatorType[],
			dxFilterOperators: DxFilterOperatorType[]
		}> = {} as any;
		// const result: Record<CubeJsFilterOperatorType, {
		// 	cubeJs: Record<CubeJsDataTypes, boolean>,
		// 	devExtremeOperator: Record<DxFilterOperatorType, boolean>
		// }> = {} as any;
		const operators = Object
			.values(this.cubeFilterOperatorsToDimensionMeasureAssociations)
			.filter(v => v.isMeasureCompatible);

		const cache: Record<CubeJsDataTypes, {
			cubeJsOperators: Record<CubeJsFilterOperatorType, boolean>,
			dxFilterOperators: Record<DxFilterOperatorType, boolean>
		}> = {} as any;
		operators.forEach(v => {
			const operator = v.operator;
			v.dataTypes.forEach(dt => {
				if (isNil(result[dt])) {
					result[dt] = {
						cubeJsOperators: [],
						dxFilterOperators: []
					};
					cache[dt] = {
						cubeJsOperators: {} as any,
						dxFilterOperators: {} as any
					};
				}
				cache[dt].cubeJsOperators[operator] = true;
				const dxFilterOperator = this.convertCubeJsOperatorToDxFilterOperator(operator);
				if (dxFilterOperator as string !== '')
					cache[dt].dxFilterOperators[dxFilterOperator] = true;
			});
		});

		Object
			.keys(result)
			.forEach((k: CubeJsDataTypes) => {
				result[k].cubeJsOperators = Object.keys(cache[k].cubeJsOperators) as CubeJsFilterOperatorType[];
				result[k].dxFilterOperators = Object.keys(cache[k].dxFilterOperators) as DxFilterOperatorType[];
			});

		return result;
	}

	/**
	 * Returns a dictionary representing the dimension/measure to [filterOperators associations for cubejs](https://cube.dev/docs/query-format#filters-operators)
	 */
	get cubeFilterOperatorsToDimensionMeasureAssociations(): Record<CubeJsFilterOperatorType, ICubeJsFilterOperatorsAssociation> {
		const result: Record<CubeJsFilterOperatorType, ICubeJsFilterOperatorsAssociation> = {
			contains: {
				dataTypes: ['string'],
				isDimensionCompatible: true,
				isMeasureCompatible: false,
				operator: 'contains'
			},
			equals: {
				dataTypes: ['boolean', 'number', 'string', 'time'],
				isDimensionCompatible: true,
				isMeasureCompatible: true,
				operator: 'equals'
			},
			notEquals: {
				dataTypes: ['boolean', 'number', 'string', 'time'],
				isDimensionCompatible: true,
				isMeasureCompatible: true,
				operator: 'notEquals'
			},
			notContains: {
				dataTypes: ['string'],
				isDimensionCompatible: true,
				isMeasureCompatible: false,
				operator: 'notContains'
			},
			gt: {
				dataTypes: ['number', 'time'],
				isDimensionCompatible: true,
				isMeasureCompatible: true,
				operator: 'gt'
			},
			gte: {
				dataTypes: ['number', 'time'],
				isDimensionCompatible: true,
				isMeasureCompatible: true,
				operator: 'gte'
			},
			lt: {
				dataTypes: ['number', 'time'],
				isDimensionCompatible: true,
				isMeasureCompatible: true,
				operator: 'lt'
			},
			lte: {
				dataTypes: ['number', 'time'],
				isDimensionCompatible: true,
				isMeasureCompatible: true,
				operator: 'lte'
			},
			set: {
				dataTypes: ['boolean', 'number', 'string', 'time'],
				isDimensionCompatible: true,
				isMeasureCompatible: true,
				operator: 'set'
			},
			notSet: {
				dataTypes: ['boolean', 'number', 'string', 'time'],
				isDimensionCompatible: true,
				isMeasureCompatible: true,
				operator: 'notSet'
			},
			inDateRange: {
				dataTypes: ['time'],
				isDimensionCompatible: true,
				isMeasureCompatible: false,
				operator: 'inDateRange'
			},
			notInDateRange: {
				dataTypes: ['time'],
				isDimensionCompatible: true,
				isMeasureCompatible: false,
				operator: 'notInDateRange'
			},
			beforeDate: {
				dataTypes: ['time'],
				isDimensionCompatible: true,
				isMeasureCompatible: false,
				operator: 'beforeDate'
			},
			afterDate: {
				dataTypes: ['time'],
				isDimensionCompatible: true,
				isMeasureCompatible: false,
				operator: 'afterDate'
			}
		};
		return result;
	}

	convertCubeJsFilterToDxFilter(cubeJsFilter: CubeJsFilter[]): any[] {
		const inputFilter = cubeJsFilter;
		if (Array.isArray(inputFilter)) {
			if (inputFilter.length === 1) {
				const filterData = first(inputFilter);
				const andFilter = filterData as LogicalAndFilter;
				const orFilter = filterData as LogicalOrFilter;
				if (isNil(andFilter.and) && isNil(orFilter.or)) {
					const fd = filterData as any;
					let memberName: any = fd.member;
					if (!isNil(memberName) && !isString(memberName) && isString(memberName.name)) {
						memberName = memberName.name;
					}
					return [memberName, this.convertCubeJsOperatorToDxFilterOperator(fd.operator), this.convertToDxFilterValue(fd.values)];
				} else {
					const result: any[] = [];
					let filterItems: any[] = andFilter.and;
					let groupOperator: GroupOperator = 'and';
					if (isNil(filterItems)) {
						filterItems = orFilter.or;
						groupOperator = 'or';
					}
					filterItems.forEach((f, idx) => {
						const r = this.convertCubeJsFilterToDxFilter([f]);
						result.push(r);
						if (idx < filterItems.length - 1)
							result.push(groupOperator);
					});
					return result;
				}
			} else if (inputFilter.length > 1) {
				const result: any[] = [];
				inputFilter.forEach(filterData => {
					const andFilter = filterData as LogicalAndFilter;
					const orFilter = filterData as LogicalOrFilter;
					if (isNil(andFilter.and) && isNil(orFilter.or)) {
						const fd = filterData as any;
						result.push([fd.member.name, fd.operator, fd.values]);
					}
				});
				return result;
			}
		}
		return [];
	}

	convertDxFilterToCubeJsFilter(dxFilter: any[]): CubeJsFilter | Record<string, never> {
		const dxFilterLength = dxFilter.length;
		if (dxFilterLength >= 3) {
			const dxFilterParts = dxFilter.map((dfp, idx) => ({
				dxFilterPart: dfp,
				index: idx,
				isEvenIndex: idx % 2 === 0,
				isOddIndex: idx % 2 === 1
			}));

			const dxOperators = dxFilterParts.filter(edf => edf.isOddIndex);
			const dxValues = dxFilterParts.filter(edf => edf.isEvenIndex);

			if (dxOperators.every(dop => this.isDxGroupOperator(dop.dxFilterPart))) {
				const groupOperator = first(dxOperators)?.dxFilterPart as GroupOperator;
				const obj = {};
				obj[groupOperator] = dxValues.map(dv => this.convertDxFilterToCubeJsFilter(dv.dxFilterPart));
				return obj as CubeJsFilter;
			} else if (dxOperators.every(dop => this.isDxFilterOperatorType(dop.dxFilterPart))) {
				const dxField = dxFilter[0];
				const dxOperator = dxFilter[1];
				const dxValue = dxFilter[2];

				const isMeasure = this.measuresSet[dxField];
				const isDimension = this.dimensionsSet[dxField];
				let operator = this.convertDxFilterOperatorToCubeJsOperator(dxOperator);
				const isFilterOperatorForField = (this.isFilterOperatorForMeasures(operator) && isMeasure)
					|| (this.isFilterOperatorForDimensions(operator) && isDimension);
				if (!isFilterOperatorForField) {
					const error = this.$t('filterBuilder.dxToCubeJS.operatorNotAllowed', operator);
					this.alertDialog(error);
					return {};
				}
				(window as any)._isDate = isDate;
				(window as any)._formatDate = formatDate;
				let values: any = undefined;
				if (Array.isArray(dxValue)) {
					values = dxValue;
					if (operator == 'inDateRange') {
						values = values.map(v => (window as any)._formatDate(v, 'YYYY-MM-DD HH:mm:ss'));
					}
				}
				else if (isString(dxValue))
					values = [dxValue];
				else if (isNumber(dxValue) || isBoolean(dxValue))
					values = [`${dxValue}`];
				else if (isDate(dxValue))
					values = [(window as any)._formatDate(dxValue, 'YYYY-MM-DD HH:mm:ss')];
				if (isNil(values)) {
					if (operator === 'equals') {
						operator = 'notSet';
					}
					else if (operator === 'notEquals') {
						operator = 'set';
					}
				}
				const filter: BinaryFilter | UnaryFilter = {
					operator,
					member: dxField,
					values
				};
				return filter;

			} else {
				const error = this.$t('filterBuilder.dxToCubeJs.invalidFilter');
				this.alertDialog(error);
				return {};
			}
		}
		return {};
	}

	convertDxFilterOperatorToCubeJsOperator(dxFilterOperator: DxFilterOperatorType): CubeJsFilterOperatorType {
		switch (dxFilterOperator) {
			case '=':
				return 'equals';
			case '<>':
				return 'notEquals';
			case 'contains':
				return 'contains';
			case 'notcontains':
				return 'notContains';
			case '>':
				return 'gt';
			case '>=':
				return 'gte';
			case '<':
				return 'lt';
			case '<=':
				return 'lte';
			case 'between':
				return 'inDateRange';
			case 'isblank':
				return 'notSet';
			case 'isnotblank':
				return 'set';
			default:
				// const error = `[ChartFilters-dc] - ${dxFilterOperator} - operator not allowed`;
				const error = `${this.$t('filterBuilder.dxToCubeJs.operatorNotAllowed', dxFilterOperator)}`;
				this.alertDialog(error);
				throw new Error(error);
		}
	}
	convertToDxFilterValue(value)
	{
		switch(value)
		{
			case undefined:
			case null:
			case 'isblank':
				return null;
			default:
				return value;
		}
	}
	convertCubeJsOperatorToDxFilterOperator(cubeJsFilterOperator: CubeJsFilterOperatorType): DxFilterOperatorType {
		switch (cubeJsFilterOperator) {
			case 'equals':
			case 'notSet':
				return '=';
			case 'notEquals':
				return '<>';
			case 'contains':
				return 'contains';
			case 'notContains':
				return 'notcontains';
			case 'gt':
				return '>';
			case 'gte':
				return '>=';
			case 'lt':
				return '<';
			case 'lte':
				return '<=';
			case 'inDateRange':
				return 'between';
			case 'set':
				return 'isblank';
			default:
				return '' as any;
		}
	}


	isDxFilterOperatorType(inputToTest: any): inputToTest is DxFilterOperatorType {
		if (isString(inputToTest)) {
			switch (inputToTest) {
				case '=':
				case '<>':
				case '<':
				case '<=':
				case '>':
				case '>=':
				case 'contains':
				case 'endswith':
				case 'notcontains':
				case 'startswith':
				case 'between':
				case 'isblank':
				case 'isnotblank':
					return true;
			}
		}
		return false;
	}
	isFilterOperatorForDimensions(cubeJsFilterOperator: CubeJsFilterOperatorType): boolean {
		return this.cubeFilterOperatorsToDimensionMeasureAssociations[cubeJsFilterOperator].isDimensionCompatible;
	}
	isFilterOperatorForMeasures(cubeJsFilterOperator: CubeJsFilterOperatorType): boolean {
		return this.cubeFilterOperatorsToDimensionMeasureAssociations[cubeJsFilterOperator].isMeasureCompatible;
	}
	isDxGroupOperator(dxOperator: any): boolean {
		return isString(dxOperator) && this.groupOperations.some(go => go === dxOperator);
	}
	setInternalFilter(newvalue: any): void {
		const dxfilter = this.convertCubeJsFilterToDxFilter(newvalue);
		this.internalFilter = dxfilter;
	}

	@Watch('cubeJsFilter')
	onCubeJsFilterChange(newFilter: CubeJsFilter, oldFilter: CubeJsFilter) {
		if (isNil(this.setFilters))
			return;
		if (isEqual(newFilter, oldFilter))
			return;
		const result: any[] = [];
		if (isNil(newFilter))
			result.push({});
		else
			result.push(newFilter);

		this.setFilters(result as any);
		this.$emit('update:cube-js-filter', result);
	}

	@Watch('filters')
	onfiltersChange(newvalue: any) {
		// done because the query-builder of cube js breaks the filter if they are complex filters
		const dxfilter = this.convertCubeJsFilterToDxFilter(newvalue);
		if (Array.isArray(dxfilter) && dxfilter.length > 1) {
			const dxF = first(dxfilter);
			if (Array.isArray(dxF) && dxF.length === 3) {
				if (isNil(first(dxF)))
					this.onCubeJsFilterChange(this.cubeJsFilter as any, newvalue);
			}
		}
	}

}

type DxFilterOperatorType =
	'=' | '<>' | '<' | '<=' | '>' | '>=' |
	'contains' | 'endswith' |
	'notcontains' | 'startswith' | 'between' |
	'isblank' | 'isnotblank'
	;

/**
 * Cube js [filter operators](https://cube.dev/docs/query-format#filters-operators)
 * @equals for: `Dimensions` | `Measures` - types: `string` | `number` | `time`
 * @notEquals for: `Dimensions` | `Measures` - types: `string` | `number` | `time`
 * @contains for: `Dimensions` - types: `string`
 * @notContains for: `Dimensions` - types: `string`
 * @gt for: `Dimensions` | `Measures` - types: `number`
 * @gte for: `Dimensions` | `Measures` - types: `number`
 * @lt for: `Dimensions` | `Measures` - types: `number`
 * @lte for: `Dimensions` | `Measures` - types: `number`
 * @set for: `Dimensions` | `Measures` - types: `string` | `number` | `time`
 * @notSet for: `Dimensions` | `Measures` - types: `string` | `number` | `time`
 * @inDateRange for: `Dimensions` - types: `time`
 * @noInDateRange for: `Dimensions` - types: `time`
 * @beforeDate for: `Dimensions` - types: `time`
 * @afterDate for: `Dimensions` - types: `time`
 */
type CubeJsFilterOperatorType =
	'set' | 'notSet'
	| 'equals'
	| 'notEquals'
	| 'contains'
	| 'notContains'
	| 'gt'
	| 'gte'
	| 'lt'
	| 'lte'
	| 'inDateRange'
	| 'notInDateRange'
	| 'beforeDate'
	| 'afterDate';

interface ICubeJsFilterOperatorsAssociation {
	operator: CubeJsFilterOperatorType;
	isDimensionCompatible: boolean;
	isMeasureCompatible: boolean;
	dataTypes: CubeJsDataTypes[];
}

type CubeJsDataTypes = 'boolean' | 'number' | 'string' | 'time';

type CubeJsFilter = BinaryFilter | UnaryFilter | LogicalOrFilter | LogicalAndFilter;

function isLogicalAndFilter(cubeFilter: CubeJsFilter): cubeFilter is LogicalAndFilter {
	return Array.isArray(cubeFilter['and']);
}
function isLogicalOrFilter(cubeFilter: CubeJsFilter): cubeFilter is LogicalOrFilter {
	return Array.isArray(cubeFilter['or']);
}
function isBinaryOrUnaryFilter(cubeFilter: CubeJsFilter): cubeFilter is BinaryFilter | UnaryFilter {
	return isString(cubeFilter['operator']);
}

type LogicalAndFilter = {
	and: (BinaryFilter | UnaryFilter | LogicalOrFilter)[];
};

type LogicalOrFilter = {
	or: (BinaryFilter | UnaryFilter | LogicalAndFilter)[];
};

type BinaryFilter = {
	dimension?: string;
	member?: string;
	operator: CubeJsFilterOperatorType;
	values: string[];
};
type UnaryFilter = {
	dimension?: string;
	member?: string;
	operator: CubeJsFilterOperatorType;
	values?: never;
};

type GroupOperator = 'and' | 'or';

interface IField {
	caption: string;
	customizeText?: () => string;
	dataField: string;
	dataType: string;
	filterOperations: DxFilterOperatorType[];
	name?: string;
	lookup?: {
		dataSource: any[];
		valueExpr: string;
		displayExpr: string;
	};
}

